import { useState, useEffect, useMemo } from 'react'
import { TrendingUp, Download } from 'lucide-react'
import { format, subMonths, parseISO, startOfMonth, addMonths, startOfYear } from 'date-fns'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { searchFilter } from '../lib/search'

export default function Sales() {
  const { activeBrand } = useAuth()
  const [salesData, setSalesData] = useState([])
  const [stuckOrders, setStuckOrders] = useState([])
  const [deliveryExceptions, setDeliveryExceptions] = useState([])
  const [totalOrderCount, setTotalOrderCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('history')
  const [search, setSearch] = useState('')
  const [rangeType, setRangeType] = useState('3mo')
  const [customFrom, setCustomFrom] = useState(format(subMonths(startOfMonth(new Date()), 2), 'yyyy-MM'))
  const [customTo, setCustomTo] = useState(format(startOfMonth(new Date()), 'yyyy-MM'))
  const [metric, setMetric] = useState('units')
  const [showMoM, setShowMoM] = useState(false)
  const [sortKey, setSortKey] = useState('total')
  const [sortDir, setSortDir] = useState('desc')

  function getRangeStart() {
    const now = new Date()
    const currentMonthStart = startOfMonth(now)
    switch (rangeType) {
      case '6mo':    return subMonths(currentMonthStart, 5)
      case 'ytd':    return startOfYear(now)
      case '12mo':   return subMonths(currentMonthStart, 11)
      case 'custom': return parseISO(customFrom + '-01')
      default:       return subMonths(currentMonthStart, 2)
    }
  }

  function getRangeEnd() {
    if (rangeType === 'custom') return parseISO(customTo + '-01')
    return startOfMonth(new Date())
  }

  // Months to display as columns (in range)
  const monthsInRange = useMemo(() => {
    const rangeStart = getRangeStart()
    const rangeEnd = getRangeEnd()
    const months = []
    let cur = startOfMonth(rangeStart)
    while (cur <= rangeEnd) {
      months.push(format(cur, 'yyyy-MM'))
      cur = addMonths(cur, 1)
    }
    return months
  }, [rangeType, customFrom, customTo])

  useEffect(() => { if (activeBrand) load() }, [activeBrand, rangeType, customFrom, customTo])

  async function load() {
    if (rangeType === 'custom' && customFrom > customTo) return
    setLoading(true)
    const rangeStart = getRangeStart()
    const rangeEnd = getRangeEnd()
    // Fetch 1 extra month back so MoM works on the first visible column
    const fetchFrom = subMonths(rangeStart, 1).toISOString()
    const fetchTo = addMonths(rangeEnd, 1).toISOString()

    const [
      { data: rpcData },
      { count: orderCount },
      { data: stuck },
      { data: exceptions }
    ] = await Promise.all([
      // Aggregated sales via RPC — raise limit to avoid PostgREST 1000-row cap cutting off later months
      supabase.rpc('get_sales_by_month_sku', {
        p_brand_id: activeBrand.id,
        p_since_date: fetchFrom,
        p_timezone: activeBrand.timezone || 'America/New_York'
      }).limit(10000),
      // Total fulfilled order count for summary card
      supabase.from('shopify_orders')
        .select('*', { count: 'exact', head: true })
        .eq('brand_id', activeBrand.id)
        .eq('fulfillment_status', 'fulfilled')
        .gte('created_at_shopify', fetchFrom)
        .lte('created_at_shopify', fetchTo),
      // Stuck orders: paid but unfulfilled
      supabase.from('shopify_orders')
        .select('shopify_order_id, order_name, created_at_shopify, fulfillment_status, financial_status')
        .eq('brand_id', activeBrand.id)
        .eq('financial_status', 'paid')
        .neq('fulfillment_status', 'fulfilled')
        .order('created_at_shopify', { ascending: true })
        .limit(500),
      // Delivery exceptions
      supabase.from('shopify_orders')
        .select('shopify_order_id, order_name, created_at_shopify, fulfilled_at, tracking_company, tracking_number, shipment_status')
        .eq('brand_id', activeBrand.id)
        .in('shipment_status', ['attempted_delivery', 'failure'])
        .limit(500)
    ])

    setSalesData(rpcData || [])
    setTotalOrderCount(orderCount || 0)
    setStuckOrders((stuck || []).filter(o => (new Date() - parseISO(o.created_at_shopify)) / 86400000 > 2))
    setDeliveryExceptions(exceptions || [])
    setLoading(false)
  }

  // Build pivot directly from RPC results — no client-side join needed
  const skuPivot = useMemo(() => {
    const skuData = {}
    for (const row of salesData) {
      if (!skuData[row.sku]) {
        skuData[row.sku] = { sku: row.sku, productName: row.product_name, months: {} }
      }
      skuData[row.sku].months[row.month] = {
        units: Number(row.units),
        revenue: Number(row.revenue),
        orderCount: Number(row.order_count)
      }
    }
    return skuData
  }, [salesData])

  // Pivot rows for the display range only, with totals and averages
  const pivotRows = useMemo(() => {
    return Object.values(skuPivot)
      .filter(row => monthsInRange.some(m => row.months[m]))
      .map(row => {
        let totalUnits = 0, totalRevenue = 0, totalOrders = 0
        for (const m of monthsInRange) {
          const d = row.months[m]
          if (d) {
            totalUnits += d.units
            totalRevenue += d.revenue
            totalOrders += d.orderCount
          }
        }
        return {
          ...row,
          totalUnits,
          totalRevenue,
          totalOrders,
          avgUnits: totalUnits / monthsInRange.length,
          avgRevenue: totalRevenue / monthsInRange.length,
          avgOrders: totalOrders / monthsInRange.length,
          avgPrice: totalUnits > 0 ? totalRevenue / totalUnits : 0
        }
      })
      .sort((a, b) => b.totalUnits - a.totalUnits)
  }, [skuPivot, monthsInRange])


  const filteredRows = useMemo(() => {
    let rows = pivotRows
    if (search) {
      // A complete SKU shows only that product; partial text matches SKU or name (lib/search)
      rows = rows.filter(searchFilter(rows, search, (r, q) => r.sku.toLowerCase().includes(q) || r.productName.toLowerCase().includes(q)))
    }
    return [...rows].sort((a, b) => {
      let av, bv
      if (sortKey === 'sku') { av = a.sku; bv = b.sku }
      else if (sortKey === 'product') { av = a.productName; bv = b.productName }
      else if (sortKey === 'total') {
        av = metric === 'revenue' ? a.totalRevenue : metric === 'orders' ? a.totalOrders : metric === 'avgPrice' ? a.avgPrice : a.totalUnits
        bv = metric === 'revenue' ? b.totalRevenue : metric === 'orders' ? b.totalOrders : metric === 'avgPrice' ? b.avgPrice : b.totalUnits
      } else if (sortKey === 'avg') {
        av = metric === 'revenue' ? a.avgRevenue : metric === 'orders' ? a.avgOrders : metric === 'avgPrice' ? a.avgPrice : a.avgUnits
        bv = metric === 'revenue' ? b.avgRevenue : metric === 'orders' ? b.avgOrders : metric === 'avgPrice' ? b.avgPrice : b.avgUnits
      } else {
        // month key
        const ad = a.months[sortKey], bd = b.months[sortKey]
        const val = (d) => !d ? null : metric === 'revenue' ? d.revenue : metric === 'orders' ? d.orderCount : metric === 'avgPrice' ? (d.units > 0 ? d.revenue / d.units : 0) : d.units
        av = val(ad); bv = val(bd)
      }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return sortDir === 'asc' ? av - bv : bv - av
    })
  }, [pivotRows, search, sortKey, sortDir, metric])

  const totals = useMemo(() => {
    let revenue = 0, units = 0
    for (const row of filteredRows) {
      for (const m of monthsInRange) {
        const d = row.months[m]
        if (d) { revenue += d.revenue; units += d.units }
      }
    }
    return { revenue, units, orders: totalOrderCount }
  }, [filteredRows, monthsInRange, totalOrderCount])

  const monthTotals = useMemo(() => {
    const result = {}
    for (const m of monthsInRange) {
      let units = 0, revenue = 0, orders = 0
      for (const row of filteredRows) {
        const d = row.months[m]
        if (d) { units += d.units; revenue += d.revenue; orders += d.orderCount }
      }
      result[m] = { units, revenue, orders }
    }
    return result
  }, [filteredRows, monthsInRange])

  function getCellValue(monthData, m) {
    if (!monthData) return null
    switch (metric) {
      case 'units':    return monthData.units
      case 'revenue':  return monthData.revenue
      case 'orders':   return monthData.orderCount
      case 'avgPrice': return monthData.units > 0 ? monthData.revenue / monthData.units : null
      default:         return monthData.units
    }
  }

  function formatCell(val) {
    if (val == null) return null
    switch (metric) {
      case 'units':    return val.toLocaleString()
      case 'revenue':  return fmt$(val)
      case 'orders':   return val.toLocaleString()
      case 'avgPrice': return fmt$(val)
      default:         return val.toLocaleString()
    }
  }

  function getMoM(row, monthKey) {
    const prevMonthKey = format(subMonths(parseISO(monthKey + '-01'), 1), 'yyyy-MM')
    const cur = getCellValue(row.months[monthKey])
    const prev = getCellValue(row.months[prevMonthKey])
    if (cur == null || prev == null || prev === 0) return null
    return Math.round(((cur - prev) / prev) * 100)
  }

  function getTotal(row) {
    switch (metric) {
      case 'units':    return row.totalUnits.toLocaleString()
      case 'revenue':  return fmt$(row.totalRevenue)
      case 'orders':   return row.totalOrders.toLocaleString()
      case 'avgPrice': return row.totalUnits > 0 ? fmt$(row.avgPrice) : '—'
      default:         return row.totalUnits.toLocaleString()
    }
  }

  function getAvg(row) {
    switch (metric) {
      case 'units':    return row.avgUnits.toLocaleString('en-US', { maximumFractionDigits: 1 })
      case 'revenue':  return fmt$(row.avgRevenue)
      case 'orders':   return row.avgOrders.toLocaleString('en-US', { maximumFractionDigits: 1 })
      case 'avgPrice': return row.totalUnits > 0 ? fmt$(row.avgPrice) : '—'
      default:         return row.avgUnits.toLocaleString('en-US', { maximumFractionDigits: 1 })
    }
  }

  function fmt$(n) {
    return '$' + Math.round(n).toLocaleString('en-US')
  }

  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortKey(key); setSortDir('desc') }
  }

  function sortIcon(key) {
    if (sortKey !== key) return <span style={{ opacity: 0.25, marginLeft: 3 }}>↕</span>
    return <span style={{ marginLeft: 3 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
  }

  function exportCSV() {
    const metricLabel = { units: 'Units', revenue: 'Revenue', orders: 'Orders', avgPrice: 'Avg Price' }[metric]
    const headers = ['SKU', 'Product', ...monthLabels.map(m => m.label), 'Total', 'Avg/Mo']
    const rows = filteredRows.map(row => {
      const cells = monthLabels.map(({ key }) => {
        const val = getCellValue(row.months[key])
        return val == null ? '' : val
      })
      return [row.sku, row.productName, ...cells, getTotal(row).replace(/[$,]/g, ''), getAvg(row).replace(/[$,]/g, '')]
    })

    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sales-${metric}-${rangeType}-${activeBrand?.slug || 'export'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const statusCount = stuckOrders.length + deliveryExceptions.length
  const monthLabels = monthsInRange.map(m => ({ key: m, label: format(parseISO(m + '-01'), 'MMM yy') }))

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Sales</h1>
          <p style={{ color: 'var(--text-2)', marginTop: 4, fontSize: 13 }}>Order history and fulfillment status</p>
        </div>
        <div className="page-header-actions" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {[
              { key: '3mo', label: '3M' },
              { key: '6mo', label: '6M' },
              { key: 'ytd', label: 'YTD' },
              { key: '12mo', label: '12M' },
              { key: 'custom', label: 'Custom' }
            ].map(({ key, label }, i, arr) => (
              <button key={key} onClick={() => setRangeType(key)} style={{
                background: rangeType === key ? 'var(--accent)' : 'var(--bg-2)',
                color: rangeType === key ? '#fff' : 'var(--text-2)',
                border: 'none',
                borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                cursor: 'pointer', padding: '6px 14px',
                fontSize: 12, fontWeight: rangeType === key ? 600 : 400,
                fontFamily: 'var(--font)'
              }}>
                {label}
              </button>
            ))}
          </div>
          {rangeType === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="month"
                className="input"
                value={customFrom}
                max={customTo}
                onChange={e => setCustomFrom(e.target.value)}
                style={{ padding: '5px 10px', fontSize: 13, width: 150 }}
              />
              <span style={{ color: 'var(--text-3)', fontSize: 13 }}>→</span>
              <input
                type="month"
                className="input"
                value={customTo}
                min={customFrom}
                max={format(startOfMonth(new Date()), 'yyyy-MM')}
                onChange={e => setCustomTo(e.target.value)}
                style={{ padding: '5px 10px', fontSize: 13, width: 150 }}
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {[
          ['history', 'Shopify Sales History'],
          ['status', `Shopify Order Status${statusCount > 0 ? ` (${statusCount})` : ''}`]
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '8px 20px',
            borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === key ? 'var(--accent)' : 'var(--text-2)',
            fontWeight: tab === key ? 600 : 400, marginBottom: -1, fontFamily: 'var(--font)'
          }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'history' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            {[
              { label: 'Gross Revenue', value: fmt$(totals.revenue) },
              { label: 'Units Sold', value: totals.units.toLocaleString() },
              { label: 'Orders', value: totals.orders.toLocaleString() }
            ].map(({ label, value }) => (
              <div key={label} className="card" style={{ padding: '16px 20px' }}>
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input"
              placeholder="Search SKU or product name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 280 }}
            />
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              {[
                { key: 'units', label: 'Units' },
                { key: 'revenue', label: 'Gross $' },
                { key: 'orders', label: 'Orders' },
                { key: 'avgPrice', label: 'Avg Price' }
              ].map(({ key, label }, i, arr) => (
                <button key={key} onClick={() => setMetric(key)} style={{
                  background: metric === key ? 'var(--accent)' : 'var(--bg-2)',
                  color: metric === key ? '#fff' : 'var(--text-2)',
                  border: 'none',
                  borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                  cursor: 'pointer', padding: '6px 14px',
                  fontSize: 12, fontWeight: metric === key ? 600 : 400,
                  fontFamily: 'var(--font)'
                }}>
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowMoM(v => !v)}
              style={{
                background: showMoM ? 'var(--accent)' : 'var(--bg-2)',
                color: showMoM ? '#fff' : 'var(--text-2)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                cursor: 'pointer', padding: '6px 14px', fontSize: 12,
                fontWeight: showMoM ? 600 : 400, fontFamily: 'var(--font)'
              }}
            >
              MoM
            </button>
            <button onClick={exportCSV} className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Download size={13} /> Export CSV
            </button>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            {filteredRows.length === 0 ? (
              <div className="empty-state">
                <TrendingUp size={32} style={{ color: 'var(--text-3)' }} />
                <p>No sales data yet — data will appear after the next sync.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    {/* Column headers — all sortable */}
                    <tr>
                      {[
                        { key: 'sku', label: 'SKU', sticky: true, left: 0, zIndex: 3, align: 'left', minWidth: 110, borderRight: true },
                        { key: 'product', label: 'Product', sticky: true, left: 110, zIndex: 3, align: 'left', minWidth: 200, borderRight: true }
                      ].map(({ key, label, sticky, left, zIndex, align, minWidth, borderRight }) => (
                        <th key={key} onClick={() => handleSort(key)} style={{ cursor: 'pointer', position: sticky ? 'sticky' : undefined, top: 0, left: sticky ? left : undefined, zIndex: zIndex || 2, background: 'var(--bg-3)', minWidth, textAlign: align, padding: '10px 12px', borderBottom: '1px solid var(--border)', borderRight: borderRight ? '1px solid var(--border)' : undefined, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-2)', userSelect: 'none' }}>
                          {label}{sortIcon(key)}
                        </th>
                      ))}
                      {monthLabels.map(({ key, label }) => (
                        <th key={key} onClick={() => handleSort(key)} style={{ cursor: 'pointer', position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-3)', textAlign: 'right', minWidth: showMoM ? 80 : 68, padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-2)', userSelect: 'none' }}>
                          {label}{sortIcon(key)}
                        </th>
                      ))}
                      <th onClick={() => handleSort('total')} style={{ cursor: 'pointer', position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-3)', textAlign: 'right', minWidth: 72, padding: '10px 12px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-2)', userSelect: 'none' }}>Total{sortIcon('total')}</th>
                      <th onClick={() => handleSort('avg')} style={{ cursor: 'pointer', position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-3)', textAlign: 'right', minWidth: 72, padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-2)', userSelect: 'none' }}>Avg/Mo{sortIcon('avg')}</th>
                    </tr>
                    {/* Totals row — pinned below headers */}
                    <tr style={{ background: 'var(--bg-4, var(--bg-3))' }}>
                      <td style={{ position: 'sticky', top: 37, left: 0, zIndex: 3, background: 'inherit', padding: '7px 12px', borderBottom: '2px solid var(--border)', borderRight: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>TOTAL</td>
                      <td style={{ position: 'sticky', top: 37, left: 110, zIndex: 3, background: 'inherit', padding: '7px 12px', borderBottom: '2px solid var(--border)', borderRight: '1px solid var(--border)' }} />
                      {monthLabels.map(({ key }) => {
                        const mt = monthTotals[key]
                        const val = !mt ? null : metric === 'revenue' ? fmt$(mt.revenue) : metric === 'orders' ? mt.orders.toLocaleString() : metric === 'avgPrice' ? (mt.units > 0 ? fmt$(mt.revenue / mt.units) : '—') : mt.units.toLocaleString()
                        return (
                          <td key={key} style={{ position: 'sticky', top: 37, zIndex: 2, background: 'inherit', textAlign: 'right', padding: '7px 12px', borderBottom: '2px solid var(--border)', fontSize: 12, fontWeight: 700 }}>
                            {val ?? <span style={{ color: 'var(--text-3)' }}>—</span>}
                          </td>
                        )
                      })}
                      <td style={{ position: 'sticky', top: 37, zIndex: 2, background: 'inherit', textAlign: 'right', padding: '7px 12px', borderBottom: '2px solid var(--border)', borderLeft: '1px solid var(--border)', fontSize: 12, fontWeight: 700 }}>
                        {metric === 'revenue' ? fmt$(totals.revenue) : metric === 'orders' ? totals.orders.toLocaleString() : metric === 'avgPrice' ? (totals.units > 0 ? fmt$(totals.revenue / totals.units) : '—') : totals.units.toLocaleString()}
                      </td>
                      <td style={{ position: 'sticky', top: 37, zIndex: 2, background: 'inherit', padding: '7px 12px', borderBottom: '2px solid var(--border)' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(row => (
                      <tr key={row.sku} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--bg-2)', fontFamily: 'var(--mono)', fontSize: 12, padding: '10px 12px', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{row.sku}</td>
                        <td style={{ position: 'sticky', left: 110, zIndex: 1, background: 'var(--bg-2)', color: 'var(--text-2)', fontSize: 13, padding: '10px 12px', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{row.productName}</td>
                        {monthLabels.map(({ key }) => {
                          const val = getCellValue(row.months[key])
                          const mom = showMoM ? getMoM(row, key) : null
                          return (
                            <td key={key} style={{ textAlign: 'right', verticalAlign: 'top', padding: '10px 12px' }}>
                              {val == null
                                ? <span style={{ color: 'var(--text-3)' }}>—</span>
                                : <>
                                    <div style={{ fontWeight: 600 }}>{formatCell(val)}</div>
                                    {showMoM && (
                                      <div style={{ fontSize: 11, marginTop: 2, color: mom == null ? 'var(--text-3)' : mom >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                                        {mom == null ? '—' : `${mom >= 0 ? '+' : ''}${mom}%`}
                                      </div>
                                    )}
                                  </>
                              }
                            </td>
                          )
                        })}
                        <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', borderLeft: '1px solid var(--border)' }}>{getTotal(row)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-2)', padding: '10px 12px' }}>{getAvg(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'status' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div className="card">
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>Stuck Orders</span>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>paid but unfulfilled &gt;2 days</span>
              {stuckOrders.length > 0 && <span className="badge badge-danger">{stuckOrders.length}</span>}
            </div>
            {stuckOrders.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>No stuck orders</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Created</th>
                      <th>Days Open</th>
                      <th>Financial</th>
                      <th>Fulfillment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stuckOrders.map(o => {
                      const days = Math.floor((new Date() - parseISO(o.created_at_shopify)) / 86400000)
                      return (
                        <tr key={o.shopify_order_id}>
                          <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{o.order_name}</td>
                          <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{format(parseISO(o.created_at_shopify), 'MMM d, yyyy')}</td>
                          <td><span className="badge badge-danger">{days}d</span></td>
                          <td><span className="badge badge-success">{o.financial_status}</span></td>
                          <td><span className="badge badge-warning">{o.fulfillment_status}</span></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>Delivery Exceptions</span>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>attempted delivery or failed</span>
              {deliveryExceptions.length > 0 && <span className="badge badge-warning">{deliveryExceptions.length}</span>}
            </div>
            {deliveryExceptions.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>No delivery exceptions</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Fulfilled</th>
                      <th>Carrier</th>
                      <th>Tracking</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveryExceptions.map(o => (
                      <tr key={o.shopify_order_id}>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{o.order_name}</td>
                        <td style={{ color: 'var(--text-2)', fontSize: 13 }}>
                          {o.fulfilled_at ? format(parseISO(o.fulfilled_at + 'T12:00:00'), 'MMM d, yyyy') : '—'}
                        </td>
                        <td style={{ color: 'var(--text-2)' }}>{o.tracking_company || '—'}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>{o.tracking_number || '—'}</td>
                        <td>
                          <span className={`badge ${o.shipment_status === 'failure' ? 'badge-danger' : 'badge-warning'}`}>
                            {o.shipment_status?.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
